import React from 'react';
import { Eye, Edit3, Trash2, ExternalLink } from 'lucide-react';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import EngagementActions from './EngagementActions';
import QuickRatingInterface from './QuickRatingInterface';

const categoryConfig = {
  project: {
    gradient: 'bg-gradient-to-br from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200',
  },
  learning: {
    gradient: 'bg-gradient-to-br from-rose-500 to-pink-600',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    borderColor: 'border-rose-200',
  },
  experience: {
    gradient: 'bg-gradient-to-br from-amber-400 to-orange-500',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
  },
  book: {
    gradient: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
  },
  recipe: {
    gradient: 'bg-gradient-to-br from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    icon: '🍳',
  },
  movie: {
    gradient: 'bg-slate-900',
    bgColor: 'bg-stone-50',
    textColor: 'text-stone-900',
    borderColor: 'border-stone-200',
  },
};

const complexityConfig = {
  beginner: {
    label: 'Beginner',
    description: 'Quick & easy',
  },
  intermediate: {
    label: 'Intermediate',
    description: 'Some planning needed',
  },
  portfolio: {
    label: 'Portfolio Ready',
    description: 'College-worthy project',
  },
};

export default function EnhancedRecommendationCard({
  recommendation,
  onViewDetails,
  onEdit,
  onDelete,
  onResponse,
  onStartProject,
  isParent,
  onConfirmDelete,
  onEngagementAction,
  currentStatus,
  currentUser,
  onWatchUpdate,
  watchedMovies
}) {

  // Safety check - don't render if recommendation is undefined
  if (!recommendation) {
    return null;
  }

  // Handler for engagement actions
  const handleEngagementAction = (actionId, category) => {
    if (onEngagementAction) {
      onEngagementAction(recommendation.id, actionId, category);
    }
  };
  const config = categoryConfig[recommendation.category] || categoryConfig.project;
  const complexity = complexityConfig[recommendation.complexity] || complexityConfig.beginner;
  
  // Parse steps from description or steps field
  const getPreviewSteps = () => {
    if (recommendation.steps && recommendation.steps.length > 0) {
      return recommendation.steps.slice(0, 3);
    }
    
    // Try to extract steps from description
    const stepPattern = /(?:^|\n)\s*(?:\d+[.)]\s*|[-•*]\s*)(.+)/gm;
    const matches = [...(recommendation.description || '').matchAll(stepPattern)];
    
    return matches.slice(0, 3).map((match, index) => ({
      id: `step-${index}`,
      text: match[1].trim(),
      order: index + 1
    }));
  };

  const previewSteps = getPreviewSteps();
  const hasSteps = previewSteps.length > 0;

  const handleCardClick = (e) => {
    // Don't trigger if clicking on buttons
    if (e.target.closest('button')) return;
    onViewDetails(recommendation);
  };

  // Get college portfolio tag if present
  const hasCollegeTag = recommendation.tags?.some(tag => 
    tag.type === 'college' || tag.text.toLowerCase().includes('college') || tag.text.toLowerCase().includes('portfolio')
  );

  // Amazon link functionality for books
  const handleViewOnAmazon = (e) => {
    e.stopPropagation();
    const title = recommendation.title || recommendation.book_title || '';
    const author = recommendation.author || recommendation.book_author || '';
    const searchQuery = encodeURIComponent(`${title} ${author}`);
    const amazonUrl = `https://www.amazon.com/s?k=${searchQuery}&i=stripbooks`;
    window.open(amazonUrl, '_blank');
  };

  // Rotten Tomatoes link functionality for movies
  const handleViewOnRottenTomatoes = (e) => {
    e.stopPropagation();
    const title = recommendation.title || '';
    const year = recommendation.release_year || '';
    const searchQuery = encodeURIComponent(`${title} ${year}`.trim());
    const rtUrl = `https://www.rottentomatoes.com/search?search=${searchQuery}`;
    window.open(rtUrl, '_blank');
  };

  return (
    <div 
      className="bg-white rounded-2xl overflow-hidden border border-stone-200 hover:border-stone-300 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col"
      onClick={handleCardClick}
    >
      {/* Hero Section */}
      <div className={`h-48 relative ${config.gradient} overflow-hidden`}>
        {/* Feature image background */}
        {recommendation.image ? (
          <div className="absolute inset-0">
            <img 
              src={recommendation.image} 
              alt={recommendation.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          </div>
        ) : (
          <>
            {/* Decorative background elements (only when no image) */}
            <div className="absolute -top-12 -right-8 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
            <div className="absolute top-4 -left-4 w-16 h-16 bg-white bg-opacity-5 rounded-full"></div>
          </>
        )}
        
        {/* Main icon */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span className="text-6xl text-white opacity-90 group-hover:scale-110 transition-transform duration-300 drop-shadow-lg">
            {config.icon}
          </span>
        </div>
        
        {/* Difficulty badge */}
        {recommendation.complexity && (
          <div className="absolute top-4 right-4 bg-white bg-opacity-95 backdrop-blur-sm px-3 py-1.5 rounded-full z-20">
            <span className="text-xs font-semibold text-slate-900">
              {complexity.label}
            </span>
          </div>
        )}
        
        {/* College tag if applicable */}
        {hasCollegeTag && (
          <div className="absolute top-4 left-4 bg-violet-100 bg-opacity-95 backdrop-blur-sm px-2 py-1 rounded-full z-20">
            <span className="text-xs font-semibold text-violet-600">
              🎓 Portfolio
            </span>
          </div>
        )}
        
        {/* Rotten Tomatoes Scores for Movies */}
        {recommendation.category === 'movie' && (recommendation.rottenTomatoesCritic || recommendation.rottenTomatoesAudience) && (
          <div className="absolute bottom-4 left-4 flex gap-2 z-20">
            {recommendation.rottenTomatoesCritic !== undefined && (
              <div className="flex items-center justify-center w-10 h-10 bg-red-500 border-2 border-emerald-500 rounded-full shadow-lg">
                <span className="text-white text-xs font-bold">{recommendation.rottenTomatoesCritic}%</span>
              </div>
            )}
            {recommendation.rottenTomatoesAudience !== undefined && (
              <div className="flex items-center justify-center w-10 h-10 bg-white border-2 border-red-500 rounded-full shadow-lg">
                <span className="text-red-500 text-xs font-bold">{recommendation.rottenTomatoesAudience}%</span>
              </div>
            )}
          </div>
        )}
        
        {/* Parent edit controls */}
        {isParent && (
          <div className="absolute bottom-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(recommendation);
              }}
              className="p-2 bg-white bg-opacity-90 hover:bg-white rounded-lg transition-colors shadow-sm"
              title="Edit recommendation"
            >
              <Edit3 className="w-4 h-4 text-slate-600" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConfirmDelete ? onConfirmDelete(recommendation.id) : onDelete(recommendation.id);
              }}
              className="p-2 bg-white bg-opacity-90 hover:bg-red-50 rounded-lg transition-colors shadow-sm"
              title="Delete recommendation"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6 flex-1 flex flex-col">
        {/* Title and Subtitle */}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2 leading-tight">
            {recommendation.title}
          </h3>
          <p className="text-sm font-medium text-stone-500 capitalize">
            {recommendation.category === 'project' && 'Design + Engineering Project'}
            {recommendation.category === 'learning' && 'Learning Course'}
            {recommendation.category === 'experience' && 'Experience Activity'}
            {recommendation.category === 'book' && 'Book Recommendation'}
            {recommendation.category === 'recipe' && 'Recipe Guide'}
            {recommendation.category === 'movie' && (recommendation.isTV ? 'TV Show' : 'Movie')}
          </p>
        </div>

        {/* Description */}
        <p className="text-sm text-stone-600 mb-5 line-clamp-3 leading-relaxed">
          {recommendation.description}
        </p>

        {/* Tags */}
        {recommendation.category !== 'movie' && (
          recommendation.tags && recommendation.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5">
            {recommendation.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  tag.type === 'college' 
                    ? 'bg-stone-100 text-stone-900' 
                    : 'bg-stone-100 text-stone-600'
                }`}
              >
                {tag.text}
              </span>
            ))}
            {recommendation.tags.length > 3 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-500">
                +{recommendation.tags.length - 3}
              </span>
            )}
          </div>
        ))}

        {/* Recipe Ingredients Preview */}
        {recommendation.category === 'recipe' && recommendation.materials_needed && recommendation.materials_needed.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-slate-900">Ingredients</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
                {recommendation.materials_needed.length} items
              </span>
            </div>
            <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <div className="space-y-1">
                {recommendation.materials_needed.slice(0, 6).map((ingredient, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-red-500 text-xs mt-1">•</span>
                    <span className="text-sm text-stone-700 line-clamp-1">
                      {ingredient}
                    </span>
                  </div>
                ))}
                {recommendation.materials_needed.length > 6 && (
                  <div className="flex items-center gap-2">
                    <span className="text-red-300 text-xs">•••</span>
                    <span className="text-xs text-stone-500">
                      {recommendation.materials_needed.length - 6} more ingredients
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Steps Preview (for non-recipe items or recipes with steps) */}
        {hasSteps && recommendation.category !== 'recipe' && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-slate-900">Quick Start</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
                {recommendation.steps?.length || previewSteps.length} steps
              </span>
            </div>
            <div className="space-y-2">
              {previewSteps.map((step, index) => (
                <div key={step.id || index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-stone-600">
                      {step.order || index + 1}
                    </span>
                  </div>
                  <span className="text-sm text-stone-600 line-clamp-1">
                    {step.text}
                  </span>
                </div>
              ))}
              {(recommendation.steps?.length || 0) > 3 && (
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-stone-50 rounded-full flex items-center justify-center">
                    <span className="text-xs text-stone-400">•••</span>
                  </div>
                  <span className="text-xs text-stone-400">
                    {(recommendation.steps?.length || 0) - 3} more steps
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recipe Steps Preview (show both ingredients and steps for recipes) */}
        {recommendation.category === 'recipe' && hasSteps && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-slate-900">Instructions</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
                {recommendation.steps?.length || previewSteps.length} steps
              </span>
            </div>
            <div className="space-y-2">
              {previewSteps.slice(0, 3).map((step, index) => (
                <div key={step.id || index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-red-600">
                      {step.order || index + 1}
                    </span>
                  </div>
                  <span className="text-sm text-stone-600 line-clamp-1">
                    {step.text}
                  </span>
                </div>
              ))}
              {(recommendation.steps?.length || 0) > 3 && (
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-50 rounded-full flex items-center justify-center">
                    <span className="text-xs text-red-300">•••</span>
                  </div>
                  <span className="text-xs text-stone-400">
                    {(recommendation.steps?.length || 0) - 3} more steps
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Meta Information */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100 mt-auto">
          <div className="flex items-center gap-2">
            <AvatarOrInitial
              avatar={recommendation.authorAvatar}
              name={recommendation.authorName}
              size="sm"
            />
            <div className="text-xs">
              <div className="font-medium text-slate-900">
                {recommendation.authorName}
              </div>
              <div className="text-stone-500 flex items-center gap-1">
                {recommendation.targetKids?.length > 0 ? (
                  <>
                    <span className="text-xs">For</span>
                    <div className="flex -space-x-1">
                      {recommendation.targetKids.map((kid) => (
                        <AvatarOrInitial
                          key={kid.id}
                          avatar={kid?.avatar}
                          name={kid?.name}
                          size="xs"
                          className="ring-1 ring-white"
                          title={kid?.name}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  'For everyone'
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {/* Amazon button for books */}
            {recommendation.category === 'book' && (
              <button
                onClick={handleViewOnAmazon}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                title="View on Amazon"
              >
                <ExternalLink className="w-3 h-3" />
                Amazon
              </button>
            )}

            {/* Rotten Tomatoes button for movies */}
            {recommendation.category === 'movie' && (
              <button
                onClick={handleViewOnRottenTomatoes}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors"
                title="View on Rotten Tomatoes"
              >
                <ExternalLink className="w-3 h-3" />
                🍅 RT
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(recommendation);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Eye className="w-3 h-3" />
              View Details
            </button>
          </div>
        </div>
        
      </div>

      {/* Integrated Engagement Actions Bar / Movie Rating */}
      {recommendation.category === 'movie' ? (
        <div className="border-t border-stone-100 bg-stone-50 px-6 py-3 rounded-b-2xl" onClick={(e) => e.stopPropagation()}>
          {(() => {
            const watchData = watchedMovies?.get(recommendation.id);
            return (
              <QuickRatingInterface
                movie={recommendation}
                userId={currentUser?.id}
                currentStatus={watchData ? 'seen' : 'unseen'}
                currentRating={watchData?.rating || null}
                onUpdate={onWatchUpdate}
              />
            );
          })()}
        </div>
      ) : !isParent && (
        <div className="border-t border-stone-100 bg-teal-50 px-6 py-3 rounded-b-2xl">
          <EngagementActions
            category={recommendation.category}
            onAction={handleEngagementAction}
            currentStatus={currentStatus}
            compact={true}
          />
        </div>
      )}
    </div>
  );
}